/**
 * BL-124 (ruled by Christian 2026-08-07; absorbs BL-117): one-click presets on the Tasks list, with the active one ALWAYS visible.
 *
 * A preset is a concrete filter state over the page's own atoms (`buckets`, `assignedToId`), never a mapping onto the history strip's
 * `FilingPeriodStatus` vocabulary ("Due now"/"Missing"): the list filters on `TaskStatusBuckets`, so the presets are defined in those
 * terms (the load-bearing warning carried from BL-117). Every other filter (client, due date, obligation types, search) layers on top.
 */
export type TaskBucket = 'open' | 'completed' | 'cancelled';

export interface TaskPreset {
  key: 'myOpen' | 'allOpen' | 'myCompleted' | 'allCompleted';
  buckets: TaskBucket[];
  /** true: assigned to the signed-in staff member; false: anyone. */
  mine: boolean;
}

export const TASK_PRESETS: readonly TaskPreset[] = [
  { key: 'myOpen', buckets: ['open'], mine: true },
  { key: 'allOpen', buckets: ['open'], mine: false },
  { key: 'myCompleted', buckets: ['completed'], mine: true },
  { key: 'allCompleted', buckets: ['completed'], mine: false },
];

/** The default view on opening when no preferences are saved (BL-124's proposal: what staff already saw, now stated). */
export const DEFAULT_TASK_PRESET: TaskPreset['key'] = 'myOpen';

/** The filter state a preset sets: status buckets and assignee. `meId` is the signed-in staff member's systemuser id. */
export function presetState(preset: TaskPreset, meId: string | null): { buckets: TaskBucket[]; assignedToId: string } {
  return { buckets: [...preset.buckets], assignedToId: preset.mine ? (meId ?? '') : '' };
}

/**
 * The preset the current state matches, or null ("custom"). Exact match on BOTH atoms: a list showing open + cancelled, or another
 * person's tasks, is not "All open" or "My open", and saying so would be the invisible state this item exists to remove.
 */
export function activePreset(buckets: readonly string[], assignedToId: string, meId: string | null): TaskPreset['key'] | null {
  const set = new Set(buckets);
  for (const p of TASK_PRESETS) {
    if (set.size !== p.buckets.length || !p.buckets.every((b) => set.has(b))) continue;
    if (p.mine ? !!meId && assignedToId === meId : assignedToId === '') return p.key;
  }
  return null;
}
